import { DateRangeString } from "@/types/date.types";
import { SelectOption, SelectOptionGroup } from "@/types/selectOption.types";
import { SearchField } from "@/features/search/types/searchField.types";

export const setSearchParam = (
  searchParams: URLSearchParams,
  name: string,
  elem?: string
) => {
  if (elem !== undefined && elem !== "") {
    searchParams.set(name, elem);
  } else {
    searchParams.delete(name);
  }
};

export const setSearchParamFromDateRange = (
  searchParams: URLSearchParams,
  name: string,
  elem?: DateRangeString
) => {
  let elemString = "";
  if (elem !== undefined && elem.from !== undefined && elem.to !== undefined) {
    elemString = `${elem.from},${elem.to}`;
  }

  setSearchParam(searchParams, name, elemString);
};

export const setSearchParamFromArray = (
  searchParams: URLSearchParams,
  name: string,
  elem?: string[]
) => {
  let elemString = "";
  if (elem !== undefined && elem.length > 0) {
    elemString = elem.join(",");
  }

  setSearchParam(searchParams, name, elemString);
};

export const setSearchParamFromBoolean = (
  searchParams: URLSearchParams,
  name: string,
  elem?: boolean[]
) => {
  let elemString = "";
  if (elem !== undefined && elem.length > 0) {
    elemString = elem.map((value) => (value ? "t" : "f")).join(",");
  }

  setSearchParam(searchParams, name, elemString);
};

export const getParamAsString = (
  searchParams: URLSearchParams,
  key: string
) => {
  return searchParams.get(key) ?? "";
};

export const getParamAsArray = (searchParams: URLSearchParams, key: string) => {
  const value = searchParams.get(key)?.split(",");
  return value ?? [];
};

export const getParamAsBoolean = (
  searchParams: URLSearchParams,
  key: string,
  defaultValue: boolean | null = true
) => {
  if (defaultValue === null) {
    return [];
  }

  const value = searchParams.get(key)?.split(",");
  return value?.map((value) => value === "t") ?? [defaultValue];
};

export const getParamAsNumber = (
  searchParams: URLSearchParams,
  key: string,
  defaultValue: number = 1
) => {
  const value = searchParams.get(key);
  return value ? parseInt(value) : defaultValue;
};

export const getParamAsDateRange = (
  searchParams: URLSearchParams,
  key: string
) => {
  const value = searchParams.get(key)?.split(",");
  if (value === undefined || value.length !== 2)
    return {
      from: undefined,
      to: undefined,
    };
  return {
    from: value[0],
    to: value[1],
  };
};

export const joinArray = (array: string[] | undefined) => {
  if (array === undefined) return undefined;
  return array.join(",");
};

export const convertSearchFieldToSearchFieldGroup = (
  fields: SearchField[] | SelectOption[]
): SelectOptionGroup[] => {
  // Extract the unique sections from the fields
  const sections = [...new Set(fields.map((field) => field.section))];
  // For each section, extract the fields and convert them to SelectOptionTypes
  return sections.map((section) => ({
    label: section ?? "",
    options: fields
      .filter((field) => field.section === section)
      .sort((a, b) => a.label.localeCompare(b.label)),
  }));
};

export const setCookie = (name: string, value: string, days?: number) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/`;
};

export const getCookie = (name: string) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");
  for (let cookie of ca) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.includes(nameEQ)) {
      return trimmedCookie
        .substring(nameEQ.length, trimmedCookie.length)
        .replace(/"/g, "");
    }
  }

  return null;
};

export const eraseCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const setSessionStorage = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
};

export const getSessionStorage = (key: string) => {
  return sessionStorage.getItem(key);
};

export const removeSessionStorage = (key: string) => {
  sessionStorage.removeItem(key);
};

export const getCurrentSpaceId = () => {
  const path = window.location.pathname.split("/");
  const spaceIdIndex = path.indexOf("sp");
  return path[spaceIdIndex + 1]
    ? path[spaceIdIndex + 1]
    : getSessionStorage("currentSpaceId");
};

export const addSpaceToUrl = (url: string) => {
  const spaceId = getCurrentSpaceId();
  return `/sp/${spaceId}${url}`;
};

export const removeSpaceFromUrl = (url: string) => {
  const path = window.location.pathname.split("/");
  const spaceIdIndex = path.indexOf("sp");
  return url.replace(`/sp/${path[spaceIdIndex + 1]}`, "");
};

export function setLocalStorageWithExpiry(
  key: string,
  value: string,
  ttl: number
) {
  const item = {
    value: value,
    expiry: new Date().getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

export function getLocalStorageWithExpiry(key: string) {
  const itemString = window.localStorage.getItem(key);
  if (!itemString) return null;

  const item = JSON.parse(itemString);
  const isExpired = new Date().getTime() > item.expiry;

  if (isExpired) {
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
}
